import { useEffect, useState } from "react"
import { Button } from "../../components/ui/Button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card"
import { Input } from "../../components/ui/Input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table"
import { TableSkeleton } from "../../components/ui/Skeleton"
import { EmptyState } from "../../components/ui/EmptyState"
import { fr } from "../../locales/fr"
import { getAdminUsers, createAdminUser } from "../../services/adminUserService"

const ROLE_OPTS = [
  { value: "ROLE_OWNER", label: fr.userAccounts.roleOwner },
  { value: "ROLE_ADMIN", label: fr.userAccounts.roleAdmin },
  { value: "ROLE_EMPLOYEE", label: fr.userAccounts.roleEmployee },
  { value: "ROLE_COACH", label: fr.userAccounts.roleCoach },
]

export default function SettingsUsers() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    roles: ["ROLE_EMPLOYEE"],
  })

  const load = async () => {
    setLoading(true)
    try {
      const data = await getAdminUsers()
      setRows(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error(e)
      setRows([])
    }
    setLoading(false)
  }

  useEffect(() => {
    load()
  }, [])

  const toggleRole = (value) => {
    setForm((prev) => {
      const set = new Set(prev.roles)
      if (set.has(value)) set.delete(value)
      else set.add(value)
      const next = Array.from(set)
      return { ...prev, roles: next.length ? next : ["ROLE_EMPLOYEE"] }
    })
  }

  const submit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await createAdminUser({
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        roles: form.roles,
      })
      setForm({ email: "", password: "", name: "", roles: ["ROLE_EMPLOYEE"] })
      await load()
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || "Erreur")
    }
    setSaving(false)
  }

  const formatRoles = (roles) => {
    if (!Array.isArray(roles)) return ""
    const map = {
      ROLE_OWNER: fr.userAccounts.roleOwner,
      ROLE_ADMIN: fr.userAccounts.roleAdmin,
      ROLE_EMPLOYEE: fr.userAccounts.roleEmployee,
      ROLE_COACH: fr.userAccounts.roleCoach,
      ROLE_USER: "User",
    }
    return roles.filter((r) => r !== "ROLE_USER").map((r) => map[r] || r).join(", ")
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{fr.userAccounts.title}</h1>
        <p className="max-w-2xl text-muted-foreground">{fr.userAccounts.subtitle}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{fr.userAccounts.create}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">{fr.userAccounts.name}</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{fr.userAccounts.email}</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{fr.userAccounts.password}</label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                autoComplete="new-password"
              />
              <p className="text-xs text-muted-foreground">{fr.userAccounts.passwordHint}</p>
            </div>
            <div className="space-y-2 md:col-span-2">
              <span className="text-sm font-medium">{fr.userAccounts.roles}</span>
              <div className="mt-2 flex flex-wrap gap-3">
                {ROLE_OPTS.map((r) => (
                  <label key={r.value} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.roles.includes(r.value)}
                      onChange={() => toggleRole(r.value)}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <Button type="submit" disabled={saving}>
                {fr.userAccounts.create}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">
              <TableSkeleton rows={5} columns={3} />
            </div>
          ) : rows.length === 0 ? (
            <div className="p-6">
              <EmptyState title={fr.userAccounts.empty} />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{fr.userAccounts.tableEmail}</TableHead>
                  <TableHead>{fr.userAccounts.tableName}</TableHead>
                  <TableHead>{fr.userAccounts.tableRoles}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.email}</TableCell>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-muted-foreground">{formatRoles(u.roles)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
